import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { AddressCargo } from 'src/app/core/interfaces/addressCargo';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { CargoApproval } from 'src/app/core/interfaces/cargo-approval';
import { CargoApprovalRequest } from 'src/app/core/interfaces/cargo-approval-request';
import { DateManager } from 'src/app/core/managers/date.manager';
import { Patterns } from 'src/app/core/resources/patterns';
import { Utils } from 'src/app/core/resources/utils';
import { AuthService } from 'src/app/core/services/authentication.service';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { CargoDetailService } from 'src/app/modules/cargo/cargo-detail/cargo-detail.service';
import { DateFormatPipe } from 'src/app/core/pipe/dateFormat.pipe';
import { CargoManager } from 'src/app/core/managers/cargo.manager';
import { ConsignmentCargo } from 'src/app/core/interfaces/consignmentCargo';
import { recalculateArrivalDepartureWhenNeeded } from './approve-cargo-dates.util';
import { Destination } from 'src/app/core/interfaces/destination';
import { Permission } from 'src/app/core/resources/permission';
import { FreightListService } from 'src/app/modules/cargo/cargo-list/cargo-list.service';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Companies } from 'src/app/core/resources/companies';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FingerprintHistory, FingerprintHistoryDTOMultiple } from 'src/app/core/interfaces/fingerprintHistory';
import { Fingerprint } from 'src/app/core/interfaces/fingerprint';
@Component({
  selector: 'app-approve-cargo-dialog',
  templateUrl: './approve-cargo-dialog.component.html',
  styleUrls: ['./approve-cargo-dialog.component.scss'],
  providers: [DateFormatPipe, CargoManager]
})
export class ApproveCargoDialogComponent implements OnInit, OnDestroy {
  public readonly currentDate = new Date();
  cargoConsignments: { [key: string]: { [key: string]: ConsignmentCargo } } = {};
  formMinistryCargos: FormGroup;
  loading = true;
  today: Date = new Date();
  initialValues: { [consignmentId: string]: any } = {};
  private readonly syncUploadTimesSubscriptions = new Subscription();

  cargosRNDC: Array<Cargo> = []; // Tienen remesa
  cargosNoRNDC: Array<Cargo> = []; // No tienen remesa
  lastAddressOrigin: AddressCargo;
  minDownloadArrival: Date;
  permission = Permission;
  reasonOptions = [];
  onlyReemit = false;
  fingerprintApprovalList: { [consignmentId: string]: { fingerprint: Fingerprint, open: boolean } } = {};
  // Razón por la que el botón "Aprobar" está deshabilitado. Se calcula de forma reactiva (no en cada
  // ciclo de detección de cambios) para evitar que findErrors() recorra todo el form y congele la UI.
  disabledReason = '';
  // Cache de fechas mínimas: devuelve SIEMPRE la misma referencia Date para los mismos inputs.
  // Imprescindible: el [min] del datetime-picker corre su setter (y revalida el control, emitiendo
  // valueChanges/statusChanges) cada vez que cambia la referencia. Sin cache, devolver un Date nuevo
  // por ciclo dispara un bucle infinito de detección de cambios.
  private readonly _minDateCache = new Map<string, Date>();

  constructor(
    public dialogRef: MatDialogRef<ApproveCargoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      cargos: Array<Cargo>,
      manual_consignment_dates: boolean,
      title: string,
      description: string,
      type: 'logisticTimesModal' | 'approveCargoModal'
    },
    public utils: Utils,
    private authService: AuthService,
    private spinner: NgxSpinnerService,
    private snackbarService: SnackBarService,
    private fb: FormBuilder,
    private patterns: Patterns,
    private dialog: MatDialog,
    private cargoDetailService: CargoDetailService,
    public dateFormatPipe: DateFormatPipe,
    public cargoManager: CargoManager,
    private freightListService: FreightListService,
    public permissionRole: PermissionRole
  ) {

    this.spinner.show();
    this.cargosRNDC = (this.data.cargos as Array<Cargo>).filter((cargo: Cargo) => cargo.ministry && !this.utils.isSameCity(cargo));
    this.cargosNoRNDC = (this.data.cargos as Array<Cargo>).filter((cargo: Cargo) => !cargo.ministry || this.utils.isSameCity(cargo));
  }

  async ngOnInit() {
    // Crear el formulario con estructura dinámica
    await this.getLogisticsTimeReasons();
    this.formMinistryCargos = this.fb.group({
      cargos: this.fb.array([]),
      userId: this.authService.getUserSession().information.document,
      userName: this.authService.getUserSession().information.name,
      receiptDateDocuments: new Date()
    });
    await this.getCargoConsignments();
    this.initData();

    // Calcula la razón de deshabilitado solo cuando el form cambia (no en cada ciclo de CD).
    // debounceTime evita recalcular en ráfagas mientras se construye/edita el formulario.
    this.syncUploadTimesSubscriptions.add(
      this.formMinistryCargos.valueChanges.pipe(debounceTime(200)).subscribe(() => this.updateDisabledReason())
    );
    this.updateDisabledReason();

    this.loading = false;
    this.spinner.hide();
  }

  /** Recalcula y cachea la razón de deshabilitado del botón Aprobar. Protegido para no romper la UI. */
  private updateDisabledReason(): void {
    try {
      this.disabledReason = this.getDisabledReason(true);
    } catch (e) {
      console.error('[approve-cargo-dialog] updateDisabledReason falló', e);
      this.disabledReason = '';
    }
  }


  async getLogisticsTimeReasons() {
    try {
      const response = await this.freightListService.getLogisticsTimeReasons().toPromise();
      this.reasonOptions = response && response.catalog ? response.catalog : [];
    } catch (error) {
      this.reasonOptions = [];
      console.error("error getting logistics time reasons", error);
    }
  }

  private async getCargoConsignments() {
    for (const cargo of this.cargosRNDC) {
      let consignments: ConsignmentCargo[] = [];
      try { consignments = await this.cargoDetailService.getCargoConsignments(cargo.id, 'Created').toPromise() } catch (error) { }
      if (!consignments || !consignments.length) return this.cargoConsignments = {};
      const individualConsignments = {};
      consignments.forEach(consignment => {
        individualConsignments[consignment.id] = consignment;
      });
      this.cargoConsignments[cargo.id] = individualConsignments;
    }
  }

  private initData() {
    // Obtener la referencia al control 'cargos'
    const cargosFormArray = this.formMinistryCargos.get('cargos') as FormArray;

    // Iterar sobre cada cargo en la estructura de datos y agregarlo al formulario
    this.cargosRNDC.forEach(async cargo => {
      cargosFormArray.push(await this.createFormGroupCargo(cargo));
    });
  }

  private async createFormGroupCargo(cargo: Cargo) {
    this.lastAddressOrigin = cargo.cargoFeature.uploadDownload.origin.addresses[cargo.cargoFeature.uploadDownload.origin.addresses.length - 1];
    const firstConsignment = this.cargoConsignments[cargo.id][Object.keys(this.cargoConsignments[cargo.id])[0]];
    const timePact = firstConsignment && firstConsignment.load && this.utils.isDefined(firstConsignment.load.hours)
      ? firstConsignment.load.hours
      : this.lastAddressOrigin.timePact;
    const minutePact = firstConsignment && firstConsignment.load && this.utils.isDefined(firstConsignment.load.minutes)
      ? firstConsignment.load.minutes
      : this.lastAddressOrigin.minutePact;
    this.minDownloadArrival = this.cargoManager.getDepartureDate(this.lastAddressOrigin.time, timePact, minutePact, cargo.dateLoad);
    const destinations = cargo.cargoFeature.uploadDownload.destination;
    const consignments = [];
    /*const isCargoWithoutInitialCompliment: boolean = destinations.some(dest => {
      return dest.addresses.some(add => {
        const date = DateManager.stringToDate(`${dest.downloadDate.slice(0, 10)} ${add.time} +0500`);
        const less72hrs = DateManager.isBefore(new Date(), DateManager.add(date, 3, 'days'));
        return add && add.consignments && add.consignments.length && !(add.dateTimeDownload && add.dateTimeDownload[0]) && less72hrs;
      })
    })*/

    //if (isCargoWithoutInitialCompliment)
    //  await this.makeInitialCompliment(cargo);
    destinations.forEach((dest, i) => {
      dest.addresses.forEach((add, j) => {
        if (add && add.consignments && add.consignments.length) {
          add.consignments.forEach((consignment, k) => {
            const consigmentForm = this.createFormGroupConsignment(cargo, add, i, j, dest, consignment);
            !!consigmentForm && consignments.push(consigmentForm);
          })
        }
      })
    })
    const consignmentFormArray = this.fb.array(consignments);
    this.registerUploadTimesSyncAcrossConsignments(consignmentFormArray);
    const form = this.fb.group({
      cargoId: [cargo.id],
      requiresMinistryLogisticTimes: [cargo.requiresMinistryLogisticTimes],
      consignment: consignmentFormArray,
    });
    const updatedForm = await this.patchLastFingerprintApproval(form, cargo.id);
    return updatedForm;
  }

  private registerUploadTimesSyncAcrossConsignments(consignmentFormArray: FormArray): void {
    if (consignmentFormArray.length < 2) return;
    const uploadFields: Array<'arrival' | 'entry' | 'departure'> = ['arrival', 'entry', 'departure'];
    const uploadChangeDetailFields: Array<'reason' | 'observation'> = ['reason', 'observation'];
    consignmentFormArray.controls.forEach((consignmentCtrl) => {
      const consignmentFg = consignmentCtrl as FormGroup;
      const uploadGroup = consignmentFg.get('upload') as FormGroup;
      if (!uploadGroup) return;
      [...uploadFields, ...uploadChangeDetailFields].forEach((field) => {
        const control = uploadGroup.get(field);
        if (!control) return;
        const sub = control.valueChanges.subscribe((newVal) => {
          consignmentFormArray.controls.forEach((otherCtrl) => {
            if (otherCtrl === consignmentCtrl) return;
            const otherUpload = (otherCtrl as FormGroup).get('upload') as FormGroup;
            if (!otherUpload) return;
            const otherField = otherUpload.get(field);
            if (!otherField || otherField.disabled) return;
            if (this.sameUploadFieldValue(field, otherField.value, newVal)) return;
            otherField.setValue(newVal, { emitEvent: false });
            otherUpload.updateValueAndValidity({ emitEvent: false });
            const otherConsignmentFg = otherCtrl as FormGroup;
            const downloadArrival = otherConsignmentFg.get('download.arrival');
            if (downloadArrival && !downloadArrival.disabled) {
              downloadArrival.updateValueAndValidity({ emitEvent: false });
            }
          });
        });
        this.syncUploadTimesSubscriptions.add(sub);
      });
    });
  }

  private async patchLastFingerprintApproval(form: FormGroup, cargoId: string) {
    const lastFingerprintApproval = await this.getLastFingerprintApproval(cargoId);
    if (lastFingerprintApproval && lastFingerprintApproval.approval && lastFingerprintApproval.approval.consignment &&
      lastFingerprintApproval.approval.consignment.length && lastFingerprintApproval.fingerprint) {
      (form.get('consignment') as FormArray).controls.forEach((consignment: FormGroup) => {
        const consignmentId = consignment.get('id').value;
        const previousFingerprintApproval = lastFingerprintApproval.approval.consignment.find(c => c.id === consignmentId);
        const dateFieldsToPatch = ['upload.arrival', 'upload.entry', 'upload.departure', 'download.arrival', 'download.entry', 'download.departure'];
        const otherFieldsToPatch = ['upload.reason', 'upload.observation', 'download.reason', 'download.observation'];
        const durationTimeFieldsToPatch = ['upload.startDateSource', 'upload.endDateSource', 'download.startDateSource', 'download.endDateSource'];
        if (previousFingerprintApproval) {
          dateFieldsToPatch.forEach(field => {
            const control = consignment.get(field);
            const value = this.utils.getNestedValue(previousFingerprintApproval, field);
            if (control && value) {
              const date = Utils.isFirebaseTimestamp(value) ? Utils.firebaseTimestampToDate(value) : DateManager.stringToDate(value);
              control.setValue(date);
            }
          });
          otherFieldsToPatch.forEach(field => {
            const control = consignment.get(field);
            const value = this.utils.getNestedValue(previousFingerprintApproval, field);
            if (control && value) control.setValue(value);
          });
          durationTimeFieldsToPatch.forEach(field => {
            const control = consignment.get(field);
            if (control) control.setValue("manual");
          });
          this.fingerprintApprovalList[consignmentId] = { fingerprint: lastFingerprintApproval.fingerprint, open: true };
        }
      });
    }
    return form;
  }

  private sameUploadDateValue(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    const da = new Date(a);
    const db = new Date(b);
    if (isNaN(da.getTime()) || isNaN(db.getTime())) return false;
    return Math.floor(da.getTime() / 60000) === Math.floor(db.getTime() / 60000);
  }

  private sameUploadFieldValue(
    field: 'arrival' | 'entry' | 'departure' | 'reason' | 'observation',
    currentValue: any,
    newValue: any
  ): boolean {
    if (field === 'arrival' || field === 'entry' || field === 'departure') {
      return this.sameUploadDateValue(currentValue, newValue);
    }
    return currentValue === newValue;
  }

  private async getLastFingerprintApproval(cargoId: string): Promise<{ approval: CargoApproval, fingerprint: Fingerprint }> {
    try {
      const history: FingerprintHistory[] = await this.cargoDetailService.getFingerPrintHistory(cargoId, 'approval').toPromise();
      if (!history || !history.length) return null;
      const orderedHistory = history.sort((a, b) => {
        const dateA = DateManager.stringToDate(this.utils.getNestedValue(a, 'fingerprint.date'));
        const dateB = DateManager.stringToDate(this.utils.getNestedValue(b, 'fingerprint.date'));
        if (!dateA && !dateB) return 0;
        if (!dateA) return -1;
        if (!dateB) return 1;
        return DateManager.isBefore(dateA, dateB, 'seconds') ? -1 : 1;
      });
      const lastHistory = orderedHistory[orderedHistory.length - 1];
      return lastHistory ? { approval: lastHistory.data as CargoApproval, fingerprint: lastHistory.fingerprint } : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /*private async makeInitialCompliment(cargo: Cargo) {
    const config = new MatDialogConfig();
    config.data = {
      title: `¿El servicio ${cargo.consecutive} no tiene cumplido inicial, desea realizarlo?`,
      showNoBtn: true,
      showYesBtn: true,
      hideBtnCancel: true,
      hideBtnConfirm: true,
    };
    config.maxHeight = ModalEnum.MAX_HEIGHT;
    config.width = ModalEnum.EXTRA_SMALL_WIDTH;
    config.maxWidth = ModalEnum.MAX_WIDTH;
    config.autoFocus = false;
    const openEndCargoCheck = (await this.dialog.open(DialogComponent, config).afterClosed().toPromise());

    if (!openEndCargoCheck || !openEndCargoCheck.state) {
      this.snackbarService.openSnackBar("Un servicio sin cumplido inicial no puede continuar el proceso", "Aceptar", "alert");
      this.dialogRef.close();
    } else {
      const config = new MatDialogConfig();
      config.data = cargo;
      config.maxHeight = ModalEnum.MAX_HEIGHT;
      config.width = ModalEnum.LARGE_WIDTH;
      config.maxWidth = ModalEnum.MAX_WIDTH;
      const _modal = this.dialog.open(EndCargoCheckComponent, config);
      const result = await _modal.afterClosed().toPromise();
      if (!!result) {
        cargo = await this.cargoDetailService.detailCargoByConsecutive(`${cargo.consecutive}`).toPromise() as Cargo;
        const cargosFormArray = this.formMinistryCargos.get('cargos') as FormArray;
        cargosFormArray.push(await this.createFormGroupCargo(cargo));
      } else {
        this.snackbarService.openSnackBar("No se ha completado el cumplido inicial", "Aceptar", "alert");
        this.dialogRef.close();
      }
    }
  }*/

  private createFormGroupConsignment(cargo: Cargo, address: AddressCargo, indexDestination: number, indexAddress: number, destination: Destination, consignmentId: string): FormGroup {
    const cargoConsignment = this.cargoConsignments[cargo.id] && this.cargoConsignments[cargo.id][consignmentId]
      ? this.cargoConsignments[cargo.id][consignmentId]
      : null;
    //Cargue
    const sender = cargoConsignment && cargoConsignment.sender ? cargoConsignment.sender : null;
    let uploadControlPointId = null;
    let uploadAddressId = null;
    if (sender && sender.address) {
      const uploadAddress = cargo.cargoFeature.uploadDownload.origin.addresses.find(address => address.address === sender.address);
      if (uploadAddress) {
        uploadControlPointId = this.utils.isDefined(uploadAddress.controlPointId) ? uploadAddress.controlPointId : null;
        uploadAddressId = this.utils.isDefined(uploadAddress.id) ? Number(uploadAddress.id) : null;
      }
    }
    const lastAddressOrigin: AddressCargo = cargo.cargoFeature.uploadDownload.origin.addresses[cargo.cargoFeature.uploadDownload.origin.addresses.length - 1];

    const amountDelivered = cargoConsignment && cargoConsignment.totalWeight ? cargoConsignment.totalWeight
      : address.cargoMeasure && address.cargoMeasure.totalWeigth ? address.cargoMeasure.totalWeigth : 0;
    const uploadEstimatedTime = cargoConsignment && cargoConsignment.load && cargoConsignment.load.appointmentTime
      ? cargoConsignment.load.appointmentTime
      : lastAddressOrigin.time;
    const uploadDateLoad = cargoConsignment && cargoConsignment.load && cargoConsignment.load.date
      ? DateManager.formatDate(cargoConsignment.load.date, 'DD/MM/YYYY')
      : cargo.dateLoad;
    const arrivalDateLoad = lastAddressOrigin.durationTime && lastAddressOrigin.durationTime.startDate ? DateManager.stringToDate(lastAddressOrigin.durationTime.startDate) : null;
    let departureDateLoad = lastAddressOrigin.durationTime && lastAddressOrigin.durationTime.endDate ? DateManager.stringToDate(lastAddressOrigin.durationTime.endDate) : null;
    const loadIsLess1HourThanDeparture = departureDateLoad && arrivalDateLoad && DateManager.dateDiff(arrivalDateLoad, null, departureDateLoad, null, 'hours') < 1;
    if (this.isTeclogiUser && loadIsLess1HourThanDeparture && lastAddressOrigin.rnmmRegistered) {
      const getRandomMinutes = Math.floor(Math.random() * 31) + 30;
      departureDateLoad = DateManager.add(arrivalDateLoad, getRandomMinutes, 'minutes');
    }

    const hourPactOrigin = cargoConsignment && cargoConsignment.load && this.utils.isDefined(cargoConsignment.load.hours)
      ? cargoConsignment.load.hours : this.utils.isDefined(lastAddressOrigin.timePact) ? lastAddressOrigin.timePact : 1;
    const minutePactOrigin = cargoConsignment && cargoConsignment.load && this.utils.isDefined(cargoConsignment.load.minutes)
      ? cargoConsignment.load.minutes : this.utils.isDefined(lastAddressOrigin.minutePact) ? lastAddressOrigin.minutePact : 0;

    //Descargue
    const consignmentIndex = address.consignments.indexOf(consignmentId);
    const hasInitialCompliment = address.dateTimeDownload && address.dateTimeDownload[consignmentIndex];

    const arrivalDateDownload = address.durationTime && address.durationTime.startDate ? DateManager.stringToDate(address.durationTime.startDate) : null;
    let departureDateDownload = address.durationTime && address.durationTime.endDate ? DateManager.stringToDate(address.durationTime.endDate) : null;
    const departureIsLess1HourThanArrival = departureDateDownload && arrivalDateDownload && DateManager.dateDiff(arrivalDateDownload, null, departureDateDownload, null, 'hours') < 1;
    if (this.isTeclogiUser && departureIsLess1HourThanArrival && address.rnmmRegistered) {
      const getRandomMinutes = Math.floor(Math.random() * 31) + 30;
      departureDateDownload = DateManager.add(arrivalDateDownload, getRandomMinutes, 'minutes');
    }

    const downloadEstimatedTime = cargoConsignment && cargoConsignment.unload && cargoConsignment.unload.appointmentTime
      ? cargoConsignment.unload.appointmentTime
      : address.time;
    const hourPactDestination = cargoConsignment && cargoConsignment.unload && this.utils.isDefined(cargoConsignment.unload.hours)
      ? cargoConsignment.unload.hours : this.utils.isDefined(address.timePact) ? address.timePact : 1;
    const minutePactDestination = cargoConsignment && cargoConsignment.unload && this.utils.isDefined(cargoConsignment.unload.minutes)
      ? cargoConsignment.unload.minutes : this.utils.isDefined(address.minutePact) ? address.minutePact : 0;

    const uploadRecalc = recalculateArrivalDepartureWhenNeeded({
      arrival: arrivalDateLoad,
      departure: departureDateLoad,
      estimatedTime: uploadEstimatedTime,
      dateLoad: uploadDateLoad,
      hourPact: hourPactOrigin,
      minutePact: minutePactOrigin,
      rmmRegistered: this.utils.isDefined(lastAddressOrigin.rmmRegistered) ? lastAddressOrigin.rmmRegistered : null
    }, 'dateLoad');
    const finalUploadArrival = uploadRecalc ? uploadRecalc.arrival : arrivalDateLoad;
    const finalUploadDeparture = uploadRecalc ? uploadRecalc.departure : departureDateLoad;
    const downloadRecalc = recalculateArrivalDepartureWhenNeeded({
      arrival: arrivalDateDownload,
      departure: departureDateDownload,
      estimatedTime: downloadEstimatedTime,
      dateDownload: departureDateDownload,
      hourPact: hourPactDestination,
      minutePact: minutePactDestination,
      rmmRegistered: this.utils.isDefined(address.rmmRegistered) ? address.rmmRegistered : null
    }, 'dateDownload');
    const finalDownloadArrival = downloadRecalc ? downloadRecalc.arrival : arrivalDateDownload;
    const finalDownloadDeparture = downloadRecalc ? downloadRecalc.departure : departureDateDownload;
    try {
      const form = this.fb.group({
        id: [consignmentId, [Validators.required]],
        destinationId: [indexDestination, [Validators.required]],
        addressId: [indexAddress, [Validators.required]],
        address: address.address, // Por comodidad para imprimir las direcciones, se eliminará del fomrulario al enviarlo
        amountDelivered: [amountDelivered, [Validators.required, Validators.pattern(this.patterns.ONLY_NUMBERS.source)]],
        upload: this.fb.group({
          estimatedTime: [uploadEstimatedTime, [Validators.required]],
          dateLoad: [uploadDateLoad, [Validators.required]],
          arrival: [finalUploadArrival],
          entry: [this.calculateEntry(finalUploadArrival, finalUploadDeparture)],
          departure: [finalUploadDeparture],
          hourPact: [hourPactOrigin],
          minutePact: [minutePactOrigin],
          reason: [null],
          observation: [''],
          controlPointId: [uploadControlPointId],
          addressId: [uploadAddressId],
          rmmRegistered: [this.utils.isDefined(lastAddressOrigin.rmmRegistered) ? lastAddressOrigin.rmmRegistered : null],
          rnmmRegistered: [this.utils.isDefined(lastAddressOrigin.rnmmRegistered) ? lastAddressOrigin.rnmmRegistered : null],
          rmmErrorMessage: [lastAddressOrigin && lastAddressOrigin.rmmErrorMessage ? lastAddressOrigin.rmmErrorMessage : null],
          rnmmErrorMessage: [lastAddressOrigin && lastAddressOrigin.rnmmErrorMessage ? lastAddressOrigin.rnmmErrorMessage : null],
          startDateSource: [this.utils.getNestedValue(lastAddressOrigin, 'durationTime.startDateSource')],
          endDateSource: [this.utils.getNestedValue(lastAddressOrigin, 'durationTime.endDateSource')],
        }),
        download: this.fb.group({
          arrival: [finalDownloadArrival],
          entry: [this.calculateEntry(finalDownloadArrival, finalDownloadDeparture)],
          departure: [finalDownloadDeparture],
          estimatedTime: [downloadEstimatedTime, [Validators.required]],
          dateDownload: [finalDownloadDeparture, [Validators.required]],
          hourPact: [hourPactDestination],
          minutePact: [minutePactDestination],
          reason: [null],
          observation: [''],
          controlPointId: [address && address.controlPointId ? address.controlPointId : null],
          addressId: [indexAddress],
          destinationId: [indexDestination],
          rmmRegistered: [this.utils.isDefined(address.rmmRegistered) ? address.rmmRegistered : null],
          rnmmRegistered: [this.utils.isDefined(address.rnmmRegistered) ? address.rnmmRegistered : null],
          rmmErrorMessage: [address && address.rmmErrorMessage ? address.rmmErrorMessage : null],
          rnmmErrorMessage: [address && address.rnmmErrorMessage ? address.rnmmErrorMessage : null],
          startDateSource: [this.utils.getNestedValue(address, 'durationTime.startDateSource')],
          endDateSource: [this.utils.getNestedValue(address, 'durationTime.endDateSource')],
        })
      });
      form.get('upload.arrival').setValidators([Validators.required, this.checkMax()]);
      form.get('upload.entry').setValidators([Validators.required, this.checkDurationTimes(form.get('upload.arrival'), 'la llegada del cargue'), this.checkMax()]);
      form.get('upload.departure').setValidators([Validators.required, this.checkDurationTimes(form.get('upload.entry'), 'la entrada del cargue'), this.checkMax(form, 'uploadDeparture')]);
      form.get('download.arrival').setValidators([Validators.required, this.checkDurationTimes(form.get('upload.departure'), 'la salida del cargue'), this.checkMax()]);
      form.get('download.entry').setValidators([Validators.required, this.checkDurationTimes(form.get('download.arrival'), 'la llegada del descargue'), this.checkMax()]);
      form.get('download.departure').setValidators([Validators.required, this.checkDurationTimes(form.get('download.entry'), 'la entrada del descargue'), this.checkMax()]);
      if ((cargoConsignment && cargoConsignment.approvalInitialId) || (!cargoConsignment && hasInitialCompliment))
        form.get('download.arrival').disable();
      form.markAllAsTouched();
      // Guardar valores iniciales
      this.initialValues[consignmentId] = this.utils.clone(form.value);
      return form;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  private calculateEntry(arrival: Date, departure: Date): Date {
    if (!arrival || !departure) return arrival;
    if (DateManager.isSame(arrival, departure, 'seconds')) return arrival;
    const diffMinutes = DateManager.dateDiff(departure, null, arrival, null, 'minutes');
    if (diffMinutes === null || isNaN(diffMinutes) || diffMinutes <= 0) return arrival;
    const halfDiffMinutes = Math.floor(diffMinutes / 2);
    const entryDate = DateManager.add(arrival, halfDiffMinutes, 'minutes');
    if (!DateManager.isBefore(entryDate, departure, 'seconds')) return diffMinutes <= 1 ? arrival : DateManager.add(departure, -1, 'minutes');
    return entryDate;
  };

  getMinDownloadEntry(date: Date | string) {
    try {
      if (!date) return null;
      const base = typeof date === "string" ? date.slice(0, 16) : date.getTime();
      const key = `entry|${base}`;
      if (this._minDateCache.has(key)) return this._minDateCache.get(key);
      const value = typeof date === "string"
        ? DateManager.add(date.slice(0, 16), 1, 'minutes')
        : DateManager.add(date, 1, 'minutes');
      this._minDateCache.set(key, value);
      return value;
    } catch (e) {
      console.error('[approve-cargo-dialog] getMinDownloadEntry falló', e);
      return null;
    }
  }

  getExecutedTime(form: FormGroup, type: 'upload' | 'download'): string {
    try {
      const departure: Date = form.get(`${type}.departure`).value;
      const arrival: Date = form.get(`${type}.arrival`).value;
      if (!departure || !arrival) return '0 horas 0 minutos';
      const currentMinutes: number = DateManager.dateDiff(departure, null, arrival, null, 'minutes');
      if (currentMinutes === null || isNaN(currentMinutes)) return '0 horas 0 minutos';
      const currentMinutesFormatted = DateManager.durationFormat(currentMinutes, 'minutes');
      const result = `${currentMinutesFormatted.days ? currentMinutesFormatted.days > 1 ? currentMinutesFormatted.days + ' dias ' : '1 dia ' : ''}
      ${currentMinutesFormatted.hours ? currentMinutesFormatted.hours > 1 ? currentMinutesFormatted.hours + ' horas ' : '1 hora ' : ''}
      ${currentMinutesFormatted.minutes ? currentMinutesFormatted.minutes > 1 ? currentMinutesFormatted.minutes + ' minutos' : '1 minuto' : ''}`.trim();
      return result || '0 horas 0 minutos';
    } catch (e) {
      console.error('[approve-cargo-dialog] getExecutedTime falló', e);
      return '0 horas 0 minutos';
    }
  }

  showAlertTimePact(form: FormGroup, type: 'upload' | 'download'): string {
    try {
      const hourPact: number = form.get(`${type}.hourPact`).value || 0;
      const minutePact: number = form.get(`${type}.minutePact`).value || 0;
      const originalTimePact: number = hourPact * 60 + minutePact;
      const departure: Date = form.get(`${type}.departure`).value;
      const arrival: Date = form.get(`${type}.arrival`).value;
      const currentMinutes: number = DateManager.dateDiff(departure, null, arrival, null, 'minutes');
      return currentMinutes > originalTimePact
        ? `El tiempo ejecutado excede el tiempo pactado, esto puede afectar Sicetac`
        : '';
    } catch (e) {
      console.error('[approve-cargo-dialog] showAlertTimePact falló', e);
      return '';
    }
  }

  private checkDurationTimes(minDate: Date | AbstractControl, field: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      try {
        if (!control || !control.value || !minDate) return {};
        if (minDate instanceof Date) return DateManager.isSameOrBefore(control.value, minDate, 'seconds') ? { 'minDate': `Debe ser mayor a ${field} (${DateManager.dateToString(minDate, 'DD/MM/YYYY HH:mm')})` } : {};
        let minDateValue = minDate.value;
        return minDate.value && DateManager.isSameOrBefore(control.value, minDateValue, 'seconds') ? { 'minDate': `Debe ser mayor a ${field} (${DateManager.dateToString(minDateValue, 'DD/MM/YYYY HH:mm')})` } : {}
      } catch (e) {
        console.error('[approve-cargo-dialog] checkDurationTimes falló', e);
        return {};
      }
    }
  }

  private checkMax(form?: FormGroup, type?: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      try {
        if (!control || !control.value) return {};
        if (type === 'uploadDeparture') {
          const arrivalDateDownload = form.get('download.arrival').value;
          return !DateManager.isSameOrBefore(control.value, arrivalDateDownload, 'seconds') ? { 'maxDate': `Debe ser menor a la fecha y hora de llegada del descargue (${DateManager.dateToString(arrivalDateDownload, 'DD/MM/YYYY HH:mm')})` } : {};
        }
        return !DateManager.isSameOrBefore(control.value, this.today, 'seconds') ? { 'maxDate': `Debe ser menor a la fecha y hora actual (${DateManager.dateToString(this.today, 'DD/MM/YYYY HH:mm')})` } : {};
      } catch (e) {
        console.error('[approve-cargo-dialog] checkMax falló', e);
        return {};
      }
    }
  }

  private createConsignmentsWithoutMinistry(): Array<CargoApproval> {
    const cargos: Array<CargoApproval> = [];
    this.cargosNoRNDC.forEach(
      (cargo) => cargos.push({ cargoId: cargo.id, consignment: [] })
    );
    return cargos;
  }

  goToEditableVisual(onlyReemit: boolean = false) {
    this.onlyReemit = onlyReemit;
    this.data['manual_consignment_dates'] = true;
  }

  getDisabledReason(silent: boolean = false): string {
    const { isValid, error } = this.findErrors(silent);
    if (!isValid) return error;
    return '';
  }

  approve() {
    this.formMinistryCargos.markAllAsTouched();
    const disabledReason = this.getDisabledReason();
    if (disabledReason) return this.snackbarService.openSnackBar(disabledReason, undefined, 'alert');
    const formValue = this.utils.clone(this.formMinistryCargos.value);
    formValue.receiptDateDocuments = DateManager.dateToString(formValue.receiptDateDocuments);
    this.formatDatesCargo(formValue.cargos);
    const cargosRequestForm: CargoApprovalRequest = formValue;

    // Limpiar address y manejar secciones/reason/observation
    for (const cargo of cargosRequestForm.cargos) {
      const cargoId = cargo.cargoId;
      const requiresMinistryLogisticTimes = cargo['requiresMinistryLogisticTimes'];
      // Eliminar requiresMinistryLogisticTimes del nivel de cargo
      if (cargo['requiresMinistryLogisticTimes'] !== undefined)
        delete cargo['requiresMinistryLogisticTimes'];
      for (const consignment of cargo.consignment) {
        const consignmentId = consignment['id'];
        const showUploadSection = this.shouldShowSection(cargoId, consignmentId, 'upload');
        const showDownloadSection = this.shouldShowSection(cargoId, consignmentId, 'download');
        if (consignment['address'])
          delete consignment['address'];
        if (consignment['upload']) {
          // Si la sección no debe mostrarse, eliminar todo upload
          if (!showUploadSection) {
            delete consignment['upload'];
          } else {
            delete consignment['upload']['estimatedTime'];
            delete consignment['upload']['dateLoad'];
            delete consignment['upload']['hourPact'];
            delete consignment['upload']['minutePact'];
            // Eliminar campos de registro de tiempos logísticos
            delete consignment['upload']['rmmRegistered'];
            delete consignment['upload']['rnmmRegistered'];
            delete consignment['upload']['rmmErrorMessage'];
            delete consignment['upload']['rnmmErrorMessage'];
            delete consignment['upload']['startDateSource'];
            delete consignment['upload']['endDateSource'];
            // Si upload no cambió o no debe mostrarse reason, eliminar reason y observation
            const showUploadReason = this.shouldShowReasonFields(cargoId, consignmentId, 'upload', requiresMinistryLogisticTimes);
            if (!showUploadReason || !this.hasGroupChanged(consignmentId, 'upload')) {
              delete consignment['upload']['reason'];
              delete consignment['upload']['observation'];
            }
          }
        }
        if (consignment['download']) {
          // Si la sección no debe mostrarse, eliminar todo download
          if (!showDownloadSection) {
            delete consignment['download'];
          } else {
            delete consignment['download']['estimatedTime'];
            delete consignment['download']['dateDownload'];
            delete consignment['download']['hourPact'];
            delete consignment['download']['minutePact'];
            // Eliminar campos de registro de tiempos logísticos
            delete consignment['download']['rmmRegistered'];
            delete consignment['download']['rnmmRegistered'];
            delete consignment['download']['rmmErrorMessage'];
            delete consignment['download']['rnmmErrorMessage'];
            delete consignment['download']['startDateSource'];
            delete consignment['download']['endDateSource'];
            // Si download no cambió o no debe mostrarse reason, eliminar reason y observation
            const showDownloadReason = this.shouldShowReasonFields(cargoId, consignmentId, 'download', requiresMinistryLogisticTimes);
            if (!showDownloadReason || !this.hasGroupChanged(consignmentId, 'download')) {
              delete consignment['download']['reason'];
              delete consignment['download']['observation'];
            }
          }
        }
      }
    }

    const cargosNoRNDC = this.createConsignmentsWithoutMinistry();
    for (const cargo of cargosNoRNDC) {
      if (!cargo.consignment.length) {
        delete cargo.consignment;
      }
      cargosRequestForm.cargos.push(cargo);
    }
    if (this.data.type === 'logisticTimesModal') delete cargosRequestForm.receiptDateDocuments;
    this.dialogRef.close(cargosRequestForm);
  }

  private findErrors(silent: boolean = false): { isValid: boolean, error?: string } {
    let controls = {
      'upload.arrival': 'fecha de llegada del cargue',
      'upload.entry': 'fecha de entrada del cargue',
      'upload.departure': 'fecha de salida del cargue',
      'download.arrival': 'fecha de llegada del descargue',
      'download.entry': 'fecha de llegada del descargue',
      'download.departure': 'fecha de salida del descargue',
      'amountDelivered': 'cantidad entregada '
    };
    if (!this.formMinistryCargos || !this.formMinistryCargos.get('cargos'))
      return { isValid: true };
    const cargos = this.formMinistryCargos.get('cargos') as FormArray;
    for (const [indexCargo, cargo] of cargos.controls.entries()) {
      const cargoId = cargo.get('cargoId').value;
      const consignments = cargo.get('consignment') as FormArray;
      for (const [indexConsigment, consignment] of consignments.controls.entries()) {
        const consignmentId = consignment.get('id').value;
        const showUploadSection = this.shouldShowSection(cargoId, consignmentId, 'upload');
        const showDownloadSection = this.shouldShowSection(cargoId, consignmentId, 'download');
        const requiresMinistryLogisticTimes = !!cargo.get('requiresMinistryLogisticTimes').value;
        // Validar reason obligatorio si el grupo cambió y debe mostrarse
        const showUploadReason = this.shouldShowReasonFields(cargoId, consignmentId, 'upload', requiresMinistryLogisticTimes);
        const showDownloadReason = this.shouldShowReasonFields(cargoId, consignmentId, 'download', requiresMinistryLogisticTimes);
        if (showUploadSection && showUploadReason && this.hasGroupChanged(consignmentId, 'upload') && !consignment.get('upload.reason').value) {
          return { isValid: false, error: `Debe seleccionar una razón del cambio en cargue (servicio ${indexCargo + 1} remesa ${indexConsigment + 1})` };
        }
        if (showDownloadSection && showDownloadReason && this.hasGroupChanged(consignmentId, 'download') && !consignment.get('download.reason').value) {
          return { isValid: false, error: `Debe seleccionar una razón del cambio en descargue (servicio ${indexCargo + 1} remesa ${indexConsigment + 1})` };
        }
        // Validar observation obligatorio si el grupo cambió y debe mostrarse
        if (showUploadSection && showUploadReason && this.hasGroupChanged(consignmentId, 'upload')) {
          const uploadObservation = consignment.get('upload.observation');
          if (!uploadObservation.value || (typeof uploadObservation.value === 'string' && uploadObservation.value.trim() === '')) {
            return { isValid: false, error: `Debe diligenciar la observación de cargue (servicio ${indexCargo + 1} remesa ${indexConsigment + 1})` };
          }
        }
        if (showDownloadSection && showDownloadReason && this.hasGroupChanged(consignmentId, 'download')) {
          const downloadObservation = consignment.get('download.observation');
          if (!downloadObservation.value || (typeof downloadObservation.value === 'string' && downloadObservation.value.trim() === '')) {
            return { isValid: false, error: `Debe diligenciar la observación de descargue (servicio ${indexCargo + 1} remesa ${indexConsigment + 1})` };
          }
        }
        for (const key of Object.keys(controls)) {
          // Saltar validación si la sección está oculta
          if (key.startsWith('upload.') && !showUploadSection) continue;
          if (key.startsWith('download.') && !showDownloadSection) continue;
          if (consignment.get(key) && consignment.get(key).errors) {
            if (consignment.get(key).errors.minDate)
              return {
                isValid: false,
                error: `La ${controls[key]} del servicio ${indexCargo + 1} remesa ${indexConsigment + 1} ${consignment.get(key).errors.minDate.toLowerCase()}`
              };
            else if (consignment.get(key).errors.maxDate)
              return {
                isValid: false,
                error: `La ${controls[key]} del servicio ${indexCargo + 1} remesa ${indexConsigment + 1} ${consignment.get(key).errors.maxDate.toLowerCase()}`
              };
            else if (silent)
              // Modo silencioso (cálculo reactivo de la razón de deshabilitado): no abrir snackbar.
              return { isValid: false, error: `La ${controls[key]} del servicio ${indexCargo + 1} remesa ${indexConsigment + 1} presenta errores, por favor verifique` };
            else if (this.utils.errorMessagesCustomized(
              consignment.get(key), `${controls[key]} del servicio ${indexCargo + 1} remesa ${indexConsigment + 1}`))
              return { isValid: false };
            else return { isValid: false, error: `Ocurrió un error al validar la ${controls[key]} del servicio ${indexCargo + 1} remesa ${indexConsigment + 1}` };
          }
        }
      }
    }
    return { isValid: true };
  }

  formatDatesCargo(cargos: any[]) {
    cargos.forEach(cargo => {
      if (cargo && cargo.consignment) {
        cargo.consignment.forEach(consignmentAdd => {
          if (consignmentAdd && consignmentAdd.upload) {
            consignmentAdd.upload.arrival = this.dateToString(new Date(consignmentAdd.upload.arrival));
            consignmentAdd.upload.departure = this.dateToString(new Date(consignmentAdd.upload.departure));
            consignmentAdd.upload.entry = this.dateToString(new Date(consignmentAdd.upload.entry));
            consignmentAdd.download.arrival = this.dateToString(new Date(consignmentAdd.download.arrival));
            consignmentAdd.download.entry = this.dateToString(new Date(consignmentAdd.download.entry));
            consignmentAdd.download.departure = this.dateToString(new Date(consignmentAdd.download.departure));
          }
        })
      }
    })
  }

  private dateToString(value: Date) {
    if (!value.getHours() && !value.getMinutes()) return DateManager.dateToString(DateManager.add(value, 1, 'minutes'));
    return DateManager.dateToString(value);
  }

  public get formMinistryCargosControls(): AbstractControl[] {
    return this.formMinistryCargos && this.formMinistryCargos.get('cargos') ? (this.formMinistryCargos.get('cargos') as FormArray).controls : [];
  }

  public getFormMinistryCargoControls(_cargo: FormGroup): AbstractControl[] {
    return _cargo && _cargo.get('consignment') ? (_cargo.get('consignment') as FormArray).controls : [];
  }

  formatMinDateLoadEntry(date: string, time: string) {
    return DateManager.add(date.slice(0, 10) + ' ' + time, 15, 'minutes');
  }

  formatMinDateDownloadDeparture(date: Date, minutePact: number, timePact: number) {
    try {
      if (!date) return null;
      const m = Number(minutePact) || 0;
      const h = Number(timePact) || 0;
      const base = date instanceof Date ? date.getTime() : new Date(date).getTime();
      const key = `departure|${base}|${m}|${h}`;
      if (this._minDateCache.has(key)) return this._minDateCache.get(key);
      let minDateValue = DateManager.add(date, m, "minutes");
      minDateValue = DateManager.add(minDateValue, h, "hours");
      this._minDateCache.set(key, minDateValue);
      return minDateValue;
    } catch (e) {
      console.error('[approve-cargo-dialog] formatMinDateDownloadDeparture falló', e);
      return null;
    }
  }

  hasGroupChanged(consignmentId: string, group: 'upload' | 'download'): boolean {
    try {
      if (this.onlyReemit) return true;
      const fields = [group + '.arrival', group + '.departure'];
      for (const field of fields) {
        if (this.hasFieldChanged(consignmentId, field)) return true;
      }
      return false;
    } catch (e) {
      console.error('[approve-cargo-dialog] hasGroupChanged falló', e);
      return false;
    }
  }

  private hasFieldChanged(consignmentId: string, field: string): boolean {
    const initial = this.initialValues[consignmentId];
    if (!initial) return false;
    const keys = field.split('.');
    let initialValue = initial;
    for (const key of keys) {
      if (initialValue && initialValue[key] !== undefined) {
        initialValue = initialValue[key];
      } else {
        return false;
      }
    }
    const cargos = this.formMinistryCargos.get('cargos') as FormArray;
    for (const cargo of cargos.controls) {
      const consignments = cargo.get('consignment') as FormArray;
      for (const c of consignments.controls) {
        if (c.get('id').value === consignmentId) {
          const control = c.get(field);
          const currentValue = control ? control.value : undefined;
          const initialDate = new Date(initialValue);
          const currentDate = new Date(currentValue);
          if (!isNaN(initialDate.getTime()) && !isNaN(currentDate.getTime())) {
            const initialTs = Math.floor(initialDate.getTime() / 60000);
            const currentTs = Math.floor(currentDate.getTime() / 60000);
            return initialTs !== currentTs;
          }
          return initialValue !== currentValue;
        }
      }
    }
    return false;
  }

  shouldShowReasonFields(cargoId: string, consignmentId: string, group: 'upload' | 'download', requiresMinistryLogisticTimes: boolean = false): boolean {
    try {
      return this.checkConsolidationType(cargoId, consignmentId, group) && requiresMinistryLogisticTimes;
    } catch (e) {
      console.error('[approve-cargo-dialog] shouldShowReasonFields falló', e);
      return false;
    }
  }

  shouldShowSection(cargoId: string, consignmentId: string, group: 'upload' | 'download'): boolean {
    try {
      // Solo aplica para logisticTimesModal
      if (this.data.type !== 'logisticTimesModal') return true;
      return this.checkConsolidationType(cargoId, consignmentId, group);
    } catch (e) {
      console.error('[approve-cargo-dialog] shouldShowSection falló', e);
      return false;
    }
  }

  private checkConsolidationType(cargoId: string, consignmentId: string, group: 'upload' | 'download'): boolean {
    const consignments = this.cargoConsignments[cargoId];
    if (!consignments) return false;
    const consignmentData: ConsignmentCargo = consignments[consignmentId];
    if (!consignmentData) return false;
    if (!consignmentData.consolidationType) return true;
    const consolidationId = consignmentData.consolidationType.id;
    // Si es Recolección (R), no mostrar en upload
    if (group === 'upload' && consolidationId === 'R') return false;
    // Si es Distribución (D), no mostrar en download
    if (group === 'download' && consolidationId === 'D') return false;
    return true;
  }

  hasUnresolvedRmmErrorForGroup(consignmentId: string, group: 'upload' | 'download', rmmRegistered: boolean): boolean {
    return rmmRegistered === false && !this.hasGroupChanged(consignmentId, group);
  }

  get permissionToSave(): boolean {
    return (this.data.type === 'logisticTimesModal' && this.permissionRole.hasPermission(this.permission.cargo.module, this.permission.cargo.editLogisticTimes) && this.data['manual_consignment_dates']) || this.data.type === 'approveCargoModal';
  }
  get isTeclogiUser(): boolean {
    return this.authService && this.authService.getCompanySaaS() &&
      Companies.companiesNIT.TECLOGI === this.authService.getCompanySaaS().companyId;
  }

  ngOnDestroy() {
    this.syncUploadTimesSubscriptions.unsubscribe();
  }

}
